import { Box, Divider, Paper, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../componentes/newAlertModal";
import FlowHeader from "../components/flowHeader";
import FlowTable from "../components/flowTable";
import { useTabStyles } from "../components/styles";
import ModalPort from "./modalPort";
import MassPorts from "./massPorts";
import { portColumns } from "./columns";

import { Port, PortPayload } from "./types";
import api from "../../../../services/api";
import { AxiosError } from "axios";

export default function Ports() {
  const [search, setSearch] = useState<string>("");
  const [ports, setPorts] = useState<Port[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMassMode, setIsMassMode] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openPortModal, setOpenPortModal] = useState<boolean>(false);
  const [editingPort, setEditingPort] = useState<Port | null>(null);
  const [savingPort, setSavingPort] = useState<boolean>(false);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedPort, setSelectedPort] = useState<Port | null>(null);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();

  useEffect(() => {
    getPorts();
  }, []);

  const getPorts = async () => {
    try {
      setLoading(true);

      const response = await api.get("/easyFlow2.0/ports");

      setPorts(Array.isArray(response.data?.data) ? response.data.data : []);
      setTableKey((key) => key + 1);
    } catch (error: any) {
      console.error(error);
      notify(
        `Error:${
          error?.message || "Error: Não foi possível se conectar com o servidor"
        } `,
        "error",
        6000,
      );
    } finally {
      setLoading(false);
    }
  };

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return ports;

    const searchLower = search.toLowerCase();
    return ports.filter((port) =>
      Object.values(port).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [ports, search]);

  const handleOpenCreate = () => {
    setEditingPort(null);
    setOpenPortModal(true);
  };

  const handleEditRow = (port: Port) => {
    setEditingPort(port);
    setOpenPortModal(true);
  };

  const handleSavePort = async (payload: PortPayload, id?: number) => {
    try {
      setSavingPort(true);

      if (id) {
        await api.put(`/easyFlow2.0/ports/${id}`, {
          ...payload,
        });
        notify("Porta atualizada com sucesso", "success");
      } else {
        await api.post(`/easyFlow2.0/ports`, {
          ...payload,
        });
        notify("Porta criada com sucesso", "success");
      }

      setOpenPortModal(false);
      setEditingPort(null);
      await getPorts();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.message ||
          "Não foi possível salvar a porta",
        "error",
      );
    } finally {
      setSavingPort(false);
    }
  };

  const handleDeleteRow = (port: Port) => {
    setSelectedPort(port);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPort) return;
    try {
      await api.delete(`/easyFlow2.0/ports/${selectedPort.id}`);
      notify("Porta removida com sucesso!", "success", 3000);
      await getPorts();
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao remover porta",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setOpenDeleteModal(false);
      setSelectedPort(null);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      {isMassMode ? (
        <MassPorts
          ports={ports}
          search={search}
          onSearchChange={setSearch}
          onBack={() => setIsMassMode(false)}
          onSaved={getPorts}
        />
      ) : (
        <>
          <FlowHeader
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar portas"
            onEnterEditMode={() => setIsMassMode(true)}
            disableEditMode={loading}
            onAdd={handleOpenCreate}
            onRefresh={getPorts}
            loading={loading}
          />
          <Divider
            flexItem
            sx={{ my: 1, borderColor: theme.palette.divider }}
          />
          <Box sx={styles.boxTable}>
            <FlowTable<Port>
              key={`ports-${tableKey}`}
              id="portsFlow"
              rows={rowsForDisplay}
              columns={portColumns}
              loadingTable={loading}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
            />
          </Box>
        </>
      )}

      <ModalPort
        open={openPortModal}
        loading={savingPort}
        portData={editingPort}
        onClose={() => {
          setOpenPortModal(false);
          setEditingPort(null);
        }}
        onSave={handleSavePort}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir a porta ${
          selectedPort ? `${selectedPort.operator} ${selectedPort.port}` : ""
        }?`}
        handleButton={handleConfirmDelete}
        handleCancel={() => setSelectedPort(null)}
        severity={"error"}
        singleButton={false}
      />
    </Paper>
  );
}
